import React, {Component, ReactElement} from 'react'
import NatPasswordRepo from './repository/NatPasswordRepo'
import {
    ContentsSubTitleInner,
    ContentsSubTitleWrapper,
    MainContainer,
    Note,
} from '../../../styledComponents/ContentsArea'
import {Nullable} from '../../../app/TypeHelpers'
import {
    DisplayBlockDiv,
    DisplayCheckItemSelectionLabel,
    DisplayCheckItemSelectionName,
    DisplayItemInput,
    DisplayItemInputLabelRequired,
    DisplayItemInputTitleRequired,
    FieldErrorLabel,
} from '../../../styledComponents/DisplayItem'
import {Messages} from '../../../Messages'
import {PrimaryStyledButton, SecondaryButton} from '../../../styledComponents/Button'
import {Box} from '@mui/material'
import {ErrorResponseBody} from '../../../network/response/ErrorResponseBody'
import {BaseHttpError} from '../../../network/errors/baseHandling/BaseHttpError'
import {RequestItemError} from '../../../network/response/RequestItemError'
import {createResidentPage, ResidentPagePropsSet} from '../../../components/shared/hoc/createResidentPage'
import {NatAsyncCallFunction} from '../../common/util/Natutil'
import {Location} from 'react-router-dom'

interface FieldsError {
    password: Error[]
    passwordConfirm: Error[]
}

interface Error {
    message: string
    hasError: boolean
}

type Field = 'password' | 'passwordConfirm'

interface Props {
    passwordRepo: NatPasswordRepo,
    location: Location
}

interface States {
    dispPasswordType: string
    password: string
    passwordConfirm: string
    changePasswordButtonDisabledFlg: boolean
    fieldsError: Partial<FieldsError>,
}

const PASSWORD_LABEL = '新しいパスワード'
const PASSWORD_CONFIRM_LABEL = '新しいパスワード（確認用）'

class NatResidentChangePasswordInputPage extends Component<Props & ResidentPagePropsSet, States> {
    constructor(props: Props & ResidentPagePropsSet) {
        super(props)
        this.state = {
            dispPasswordType: 'password',
            password: '',
            passwordConfirm: '',
            changePasswordButtonDisabledFlg: true,
            fieldsError: {},
        }

        this.onChangePassword = this.onChangePassword.bind(this)
        this.handlePassworDispTypeChange = this.handlePassworDispTypeChange.bind(this)
        this.cancelButtonClick = this.cancelButtonClick.bind(this)
        this.passwordResetButtonClick = this.passwordResetButtonClick.bind(this)
    }

    // キャンセルする
    cancelButtonClick(): void {
        this.props.navigate(
            `/resident/personalSetting`,
        )
    };

    // パスワードを変更する
    async passwordResetButtonClick(): Promise<void> {
        await this.clearMessage()

        // 相関チェック
        if (this.state.password !== this.state.passwordConfirm) {
            this.setFieldErrorMessage(
                'passwordConfirm',
                Messages.EOPCC0019.toString(PASSWORD_LABEL, PASSWORD_CONFIRM_LABEL),
            )
            return
        }
        await this.props.eventEmitter.showLoading()
        try {
            const ret: Nullable<void> = await NatAsyncCallFunction(
                this.props.passwordRepo.putPasswordAuthed.bind(
                    this.props.passwordRepo, this.state.password), this.props.navigate)
            if (ret) {
                this.props.navigate(
                    `/resident/changePasswordComplete`,
                )
            }
        } catch (HttpError) {
            const errorResponseBody: Nullable<ErrorResponseBody> = (HttpError as BaseHttpError).responseBody
            if (errorResponseBody !== null) {
                errorResponseBody.requestItemErrors.forEach((item: RequestItemError) => {
                    if (item.fields) {
                        item.fields.forEach((field) => {
                            this.setFieldErrorMessage(field, item.message)
                        })
                    }
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    };

    onChangePassword(event: React.ChangeEvent<HTMLInputElement>): void {
        let password: string = this.state.password
        let passwordConfirm: string = this.state.passwordConfirm

        switch (event.target.name) {
            case 'password':
                password = event.target.value
                break
            case 'passwordConfirm':
                passwordConfirm = event.target.value
                break
            default:
                break
        }

        if (password === '' || passwordConfirm === '') {
            this.setState({changePasswordButtonDisabledFlg: true})
        } else {
            this.setState({changePasswordButtonDisabledFlg: false})
        }

        this.setState({password: password, passwordConfirm: passwordConfirm})
    }

    // パスワードを表示する
    handlePassworDispTypeChange(event: React.ChangeEvent<HTMLInputElement>): void {
        if (event.target.checked) {
            this.setState({dispPasswordType: 'text'})
        } else {
            this.setState({dispPasswordType: 'password'})
        }
    }

    // メッセージ設定（フィールド）
    setFieldErrorMessage(field: string, message: string): void {
        if (field === 'password' || field === 'passwordConfirm') {
            let error: Error[] | undefined = this.state.fieldsError[field]
            if (error === undefined) {
                error = [{message: message, hasError: true}]
            } else {
                error.push({message: message, hasError: true})
            }
            this.setState(({fieldsError}) => (
                {
                    fieldsError: {
                        ...fieldsError,
                        [field]: error,
                    },
                }))
        }
    }

    // メッセージ取得（フィールド）
    getFieldErrorMessage(fieldId: Field): Nullable<ReactElement> {
        const field: Error[] | undefined = this.state.fieldsError[fieldId]
        if (field !== undefined) {
            return (
                <>
                    {field.map((error, index) => (
                        <FieldErrorLabel key={index}>
                            {error.message}
                        </FieldErrorLabel>
                    ))}
                </>
            )
        } else {
            return null
        }
    }

    // フィールドエラー状態取得
    getFieldErrorStatus(fieldId: Field): boolean {
        const field: Error[] | undefined = this.state.fieldsError[fieldId]
        return !!(field !== undefined && field[0] !== undefined && field[0].hasError)
    }

    // メッセージクリア
    clearMessage(): void {
        this.setState({fieldsError: {}})
    }

    render(): ReactElement {
        return (
            <>
                <ContentsSubTitleWrapper>
                    <ContentsSubTitleInner>
                        パスワード変更
                    </ContentsSubTitleInner>
                </ContentsSubTitleWrapper>
                <MainContainer>
                    <DisplayBlockDiv>
                        <DisplayItemInputLabelRequired>
                            <DisplayItemInputTitleRequired>{PASSWORD_LABEL}</DisplayItemInputTitleRequired>
                            <DisplayItemInput
                                type={this.state.dispPasswordType}
                                autoComplete={'new-password'}
                                name="password"
                                maxLength={16}
                                onChange={this.onChangePassword}
                                hasError={this.getFieldErrorStatus(`password`)}
                                autoFocus={true}/>
                            <Note>{Messages.IRESR0005.toString('')}</Note>
                        </DisplayItemInputLabelRequired>
                        {this.getFieldErrorMessage('password')}
                    </DisplayBlockDiv>
                    <DisplayBlockDiv>
                        <DisplayItemInputLabelRequired>
                            <DisplayItemInputTitleRequired>{PASSWORD_CONFIRM_LABEL}</DisplayItemInputTitleRequired>
                            <DisplayItemInput
                                type={this.state.dispPasswordType}
                                autoComplete={'new-password'}
                                name="passwordConfirm"
                                maxLength={16}
                                onChange={this.onChangePassword}
                                hasError={this.getFieldErrorStatus(`passwordConfirm`)}/>
                        </DisplayItemInputLabelRequired>
                        {this.getFieldErrorMessage('passwordConfirm')}
                    </DisplayBlockDiv>
                    <Box pb={'15px'}>
                        <DisplayCheckItemSelectionLabel>
                            <DisplayCheckItemSelectionName>
                                <input
                                    type="checkbox"
                                    onChange={this.handlePassworDispTypeChange}/>
                                パスワードを表示する
                            </DisplayCheckItemSelectionName>
                        </DisplayCheckItemSelectionLabel>
                    </Box>
                    <DisplayBlockDiv>
                        <SecondaryButton onClick={this.cancelButtonClick}>キャンセルする</SecondaryButton>
                    </DisplayBlockDiv>
                    <DisplayBlockDiv>
                        <PrimaryStyledButton
                            onClick={this.passwordResetButtonClick}
                            disabled={this.state.changePasswordButtonDisabledFlg}>
                            パスワードを変更する
                        </PrimaryStyledButton>
                    </DisplayBlockDiv>
                </MainContainer>
            </>
        )
    }
}

export default createResidentPage(NatResidentChangePasswordInputPage)

